import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Check, ChevronRight, ArrowRight, RotateCcw, Trophy,
} from '../../../components/CustomIcons';
import { Btn } from '../primitives';
import { useUI } from '../shared';
import { CHEER_LINES } from '../youthData';
import { useYouth, useXP, fx } from './youthState';
import { YouthHeader } from './YouthHome';

/* ============================================================
   Youth course node renderers + NodeRunner + celebrations
   ============================================================ */

/* ---------- Course intro ---------- */

export const CourseIntro = ({ course, back, start }) => (
  <>
    <YouthHeader title={course.title} subtitle="Course Overview" onBack={back} xp={0} hearts={3} />
    <main className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
      <div className="rounded-[12px] p-5 text-center text-white" style={{ background: 'linear-gradient(140deg,#0f2744 0%,#081827 100%)' }}>
        <div className="text-[64px]" aria-hidden="true">{course.emoji}</div>
        <h2 className="p-display text-[22px] mt-2" style={{ color: '#fafaf9', margin: 0 }}>{course.title}</h2>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-[12px] p-3 text-center bg-[#f1f5f9]">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#64748b]">Lessons</div>
          <div className="p-mono text-[20px] mt-1">{course.nodes.length}</div>
        </div>
        <div className="rounded-[12px] p-3 text-center bg-[#f1f5f9]">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#64748b]">Est. time</div>
          <div className="p-mono text-[20px] mt-1">{course.estimateMin}m</div>
        </div>
        <div className="rounded-[12px] p-3 text-center" style={{ background: 'rgba(249,115,22,0.10)' }}>
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#c2410c]">XP</div>
          <div className="p-mono text-[20px] mt-1 text-[#c2410c]">{course.xpAvailable}</div>
        </div>
      </div>
      <div className="rounded-[12px] p-3 flex items-center gap-2 bg-[#f1f5f9]">
        <span className="text-[#f97316]" aria-hidden="true">🏅</span>
        <div className="text-[13px] text-[#0f2744]">Badge on completion: <strong>{course.badge.name}</strong></div>
      </div>
    </main>
    <footer className="px-6 py-4 border-t border-[#e2e8f0] bg-[#fafaf9]">
      <Btn onClick={start} className="w-full" data-testid="youth-start-course">Start Course <ArrowRight className="w-4 h-4" aria-hidden="true" /></Btn>
    </footer>
  </>
);

/* ---------- Lesson body helpers ---------- */

export const LessonIllustration = ({ node }) => (
  <div className="rounded-[12px] p-5 text-white text-center" style={{ background: node.illustration?.bg || 'linear-gradient(140deg,#0f2744,#081827)' }}>
    <div className="text-[54px]" aria-hidden="true">{node.illustration?.emoji || node.emoji}</div>
    {node.subtitle && <div className="p-display text-[19px] mt-2" style={{ color: '#fafaf9', margin: 0 }}>{node.subtitle}</div>}
  </div>
);

export const LessonBody = ({ node }) => (
  <div className="space-y-3">
    {node.body && node.body.map((p, i) => (
      <p key={i} className="text-[17px] text-[#1c1c1e] leading-[1.55]" style={{ maxWidth: '38ch' }}>{p}</p>
    ))}
  </div>
);

export const StatCardList = ({ stats, totalLine }) => (
  <section className="rounded-[12px] p-4 bg-[#f1f5f9]">
    <h3 className="text-[11px] font-bold uppercase tracking-widest text-[#64748b] mb-2">Your cabin's appliances</h3>
    <ul className="list-none p-0 space-y-2">
      {stats.map((s, i) => (
        <li key={i} className="flex items-center justify-between text-[14px] text-[#0f2744]">
          <span>{s.icon} {s.label}</span>
          <span className="p-mono font-bold">{s.value}</span>
        </li>
      ))}
    </ul>
    {totalLine && <div className="mt-2 pt-2 border-t border-[#cbd5e1] p-mono text-[14px] text-[#c2410c] font-bold">{totalLine}</div>}
  </section>
);

/* ---------- Math challenge ---------- */

export const MathChallenge = ({ math, onSolved }) => {
  const [solved, setSolved] = useState(false);
  return (
    <section className="rounded-[12px] p-4 text-center" style={{ background: 'linear-gradient(135deg,#0f2744,#1e3a5f)', color: '#fafaf9' }}>
      <div className="p-display text-[17px]" style={{ color: '#fafaf9', margin: 0 }}>{math.title}</div>
      <p className="text-[13px] mt-2" style={{ color: 'rgba(250,250,249,0.85)' }}>{math.intro}</p>
      <div className="mt-4 p-mono text-[26px]" style={{ color: '#f97316' }}>
        {math.equation.replace('?', solved ? math.answer : '?')}
      </div>
      {solved && <div className="mt-1 text-[15px] font-semibold" style={{ color: '#f97316' }}>{math.answerFull}</div>}
      {!solved ? (
        <button
          onClick={() => { setSolved(true); onSolved(); }}
          data-testid="youth-math-solve"
          className="mt-4 min-h-[44px] rounded-[10px] px-5 font-bold text-[14px]"
          style={{ background: '#f97316', color: '#0f2744' }}
        >Tap to solve! 🧮</button>
      ) : (
        <p className="mt-3 text-[13px]" style={{ color: 'rgba(250,250,249,0.85)' }}>{math.explanation}</p>
      )}
    </section>
  );
};

/* ---------- Card flip ---------- */

export const CardFlip = ({ card, onFlip }) => {
  const [flipped, setFlipped] = useState(false);
  const handle = () => { if (!flipped) { setFlipped(true); onFlip && onFlip(); } };
  return (
    <button
      onClick={handle}
      data-testid={`youth-card-${card.title.replace(/\s+/g, '-').toLowerCase()}`}
      aria-pressed={flipped}
      className="w-full text-left rounded-[12px] p-4 transition-all"
      style={{
        background: flipped ? '#fafaf9' : '#f1f5f9',
        border: flipped ? '2px solid #f97316' : '2px solid transparent',
        minHeight: 110,
      }}
    >
      <div className="p-display text-[16px]" style={{ color: '#0f2744', margin: 0 }}>{card.title}</div>
      {!flipped && card.front && <div className="text-[36px] text-center mt-2" aria-hidden="true">{card.front}</div>}
      {flipped && card.bullets && (
        <ul className="mt-2 list-none p-0 space-y-1.5">
          {card.bullets.map((b, i) => (
            <li key={i} className="text-[13px] text-[#0f2744] flex items-start gap-2 leading-relaxed">
              <span className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: '#f97316' }} aria-hidden="true" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
      {!flipped && <div className="text-[11px] font-semibold text-[#64748b] mt-2 uppercase tracking-widest">Tap to flip</div>}
    </button>
  );
};

/* ---------- Loop diagram ---------- */

export const LoopDiagram = ({ steps }) => {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActive((a) => (a + 1) % steps.length), 900);
    return () => clearInterval(t);
  }, [steps.length]);
  return (
    <div className="rounded-[12px] p-4 bg-[#f1f5f9] flex items-center justify-center gap-2 flex-wrap">
      {steps.map((s, i) => {
        const isActive = i === active;
        return (
          <React.Fragment key={i}>
            <div className="flex flex-col items-center transition-all" style={{ opacity: isActive ? 1 : 0.4, transform: isActive ? 'scale(1.05)' : 'scale(1)' }}>
              <div className="w-14 h-14 rounded-full flex items-center justify-center text-[24px]" style={{ background: s.color, color: '#fafaf9', boxShadow: isActive ? `0 0 0 6px ${s.color}22` : 'none' }} aria-hidden="true">{s.icon}</div>
              <div className="p-mono text-[11px] mt-1 font-bold" style={{ color: s.color }}>{s.label}</div>
              <div className="text-[10px] text-[#64748b] text-center max-w-[80px] mt-0.5">{s.text}</div>
            </div>
            {i < steps.length - 1 && <ChevronRight className="w-4 h-4 text-[#94a3b8]" aria-hidden="true" />}
          </React.Fragment>
        );
      })}
    </div>
  );
};

/* ---------- Match game ---------- */

export const MatchGame = ({ config, courseId, onScore, onAllMatched }) => {
  const [selL, setSelL] = useState(null);
  const [matched, setMatched] = useState({});
  const [errorId, setErrorId] = useState(null);
  const [justMatched, setJustMatched] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [hoverR, setHoverR] = useState(null);
  const rightRefs = useRef({});
  const { fxSettings } = useYouth();
  const rights = useMemo(() => config.pairs.map((p) => p.right).sort(), [config]);
  const totalPairs = config.pairs.length;

  const attemptMatch = useCallback((leftIdx, r) => {
    const leftVal = config.pairs[leftIdx].left;
    const correct = config.pairs.find((p) => p.left === leftVal && p.right === r);
    if (correct && !matched[leftVal]) {
      const nextMatched = { ...matched, [leftVal]: r };
      setMatched(nextMatched);
      setSelL(null);
      setJustMatched(r);
      setTimeout(() => setJustMatched(null), 450);
      fx.snap(fxSettings);
      onScore && onScore(5);
      if (Object.keys(nextMatched).length >= totalPairs) {
        setTimeout(() => fx.matchComplete(fxSettings, courseId), 180);
        onAllMatched && onAllMatched();
      }
    } else {
      fx.wrong(fxSettings, courseId);
      setErrorId(`${leftIdx}-${r}`);
      setTimeout(() => setErrorId(null), 350);
    }
  }, [config.pairs, matched, totalPairs, onScore, onAllMatched, fxSettings, courseId]);

  const rightUnder = useCallback((x, y) => {
    for (const [r, el] of Object.entries(rightRefs.current)) {
      if (!el) continue;
      const b = el.getBoundingClientRect();
      if (x >= b.left && x <= b.right && y >= b.top && y <= b.bottom) return r;
    }
    return null;
  }, []);

  const onLeftPointerDown = (e, i) => {
    if (e.button !== undefined && e.button !== 0) return;
    if (matched[config.pairs[i].left]) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setDragging({ idx: i, x: e.clientX, y: e.clientY, offX: e.clientX - rect.left, offY: e.clientY - rect.top, w: rect.width, h: rect.height, moved: false });
  };

  useEffect(() => {
    if (!dragging) return undefined;
    const onMove = (e) => {
      setDragging((d) => {
        if (!d) return d;
        const moved = d.moved || Math.abs(e.clientX - d.x) > 4 || Math.abs(e.clientY - d.y) > 4;
        return { ...d, x: e.clientX, y: e.clientY, moved };
      });
      const r = rightUnder(e.clientX, e.clientY);
      setHoverR(r && !Object.values(matched).includes(r) ? r : null);
    };
    const onUp = (e) => {
      const r = rightUnder(e.clientX, e.clientY);
      if (dragging.moved && r && !Object.values(matched).includes(r)) attemptMatch(dragging.idx, r);
      setDragging(null);
      setHoverR(null);
    };
    const onCancel = () => { setDragging(null); setHoverR(null); };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
  }, [dragging, matched, rightUnder, attemptMatch]);

  const handleLeftClick = (i) => {
    if (dragging && dragging.moved) return;
    setSelL(i);
  };
  const handleR = (r) => {
    if (selL === null) return;
    attemptMatch(selL, r);
  };

  return (
    <section className="rounded-[12px] p-4 bg-[#f1f5f9]" data-testid="youth-match-game">
      <div className="p-display text-[15px] mb-2" style={{ color: '#0f2744', margin: 0 }}>{config.title}</div>
      <div className="grid grid-cols-2 gap-3">
        <ul className="list-none p-0 space-y-2">
          {config.pairs.map((p, i) => {
            const done = !!matched[p.left];
            const isBeingDragged = dragging?.idx === i && dragging.moved;
            return (
              <li key={i}>
                <button
                  disabled={done}
                  onPointerDown={(e) => onLeftPointerDown(e, i)}
                  onClick={() => handleLeftClick(i)}
                  data-testid={`youth-match-left-${i}`}
                  aria-grabbed={isBeingDragged}
                  className="w-full rounded-[10px] px-3 py-2 text-left text-[13px] font-semibold min-h-[44px] touch-none select-none"
                  style={{
                    background: done ? 'rgba(21,128,61,0.10)' : isBeingDragged ? 'rgba(249,115,22,0.15)' : selL === i ? 'rgba(249,115,22,0.15)' : '#f1f5f9',
                    color: done ? '#166534' : '#0f2744',
                    border: selL === i || isBeingDragged ? '1px solid #f97316' : '1px solid transparent',
                    cursor: done ? 'default' : 'grab',
                    opacity: isBeingDragged ? 0.35 : 1,
                  }}
                >{p.left}{done && ' ✓'}</button>
              </li>
            );
          })}
        </ul>
        <ul className="list-none p-0 space-y-2">
          {rights.map((r, i) => {
            const usedBy = Object.entries(matched).find(([, v]) => v === r);
            const done = !!usedBy;
            const shake = errorId !== null && errorId.endsWith(`-${r}`);
            const isHover = hoverR === r && dragging?.moved;
            const snapped = justMatched === r;
            return (
              <li key={i}>
                <button
                  ref={(el) => { rightRefs.current[r] = el; }}
                  disabled={done}
                  onClick={() => handleR(r)}
                  data-testid={`youth-match-right-${i}`}
                  className="w-full rounded-[10px] px-3 py-2 text-left text-[13px] font-semibold min-h-[44px] transition-transform"
                  style={{
                    background: done ? 'rgba(21,128,61,0.10)' : isHover ? 'rgba(249,115,22,0.12)' : '#f1f5f9',
                    color: done ? '#166534' : '#0f2744',
                    border: isHover ? '2px dashed #f97316' : '2px dashed transparent',
                    transform: isHover ? 'scale(1.03)' : snapped ? 'scale(1.05)' : 'scale(1)',
                    animation: shake ? 'youth-shake 320ms ease-in-out' : undefined,
                  }}
                >{r}{done && ' ✓'}</button>
              </li>
            );
          })}
        </ul>
      </div>
      {dragging && dragging.moved && (
        <div
          aria-hidden="true"
          data-testid="youth-match-ghost"
          className="text-[13px] font-semibold rounded-[10px] px-3 py-2 fixed z-[999] pointer-events-none shadow-lg flex items-center"
          style={{
            left: dragging.x - dragging.offX,
            top: dragging.y - dragging.offY,
            width: dragging.w,
            minHeight: dragging.h,
            background: '#fff',
            border: '2px solid #f97316',
            color: '#0f2744',
          }}
        >{config.pairs[dragging.idx].left}</div>
      )}
      <div className="text-[11px] text-[#94a3b8] mt-2">Drag a card onto its match — or tap one, then tap its pair.</div>
    </section>
  );
};

/* ---------- Password meter ---------- */

export const PasswordMeter = ({ config, onDone }) => {
  const [revealed, setRevealed] = useState({});
  const done = Object.keys(revealed).length >= config.examples.length;
  useEffect(() => { if (done) onDone && onDone(); }, [done, onDone]);
  return (
    <section className="rounded-[14px] p-4 bg-[#f8fafc] space-y-3">
      <div className="p-display text-[15px]" style={{ color: '#0f2744', margin: 0 }}>{config.title}</div>
      <div className="text-[12px] text-[#64748b]">{config.intro}</div>
      {config.examples.map((ex, i) => {
        const isRevealed = revealed[i];
        const tones = ['#dc2626', '#f97316', '#f59e0b', '#84cc16', '#15803d'];
        const tone = tones[Math.min(ex.strength, 4)];
        return (
          <button
            key={i}
            onClick={() => { if (!isRevealed) setRevealed((r) => ({ ...r, [i]: true })); }}
            data-testid={`youth-meter-${i}`}
            className="w-full text-left rounded-[10px] p-3 bg-white border-2 transition-all"
            style={{ borderColor: isRevealed ? tone : '#e2e8f0', minHeight: 64 }}
          >
            <div className="flex items-center justify-between">
              <span className="p-mono text-[13px] font-bold text-[#0f2744]">"{ex.pw}"</span>
              {!isRevealed && <span className="text-[11px] text-[#94a3b8] uppercase tracking-widest font-bold">Tap to test</span>}
            </div>
            {isRevealed && (
              <>
                <div className="mt-2 w-full h-2 rounded-full overflow-hidden" style={{ background: '#f1f5f9' }} aria-hidden="true">
                  <div className="h-full rounded-full" style={{ width: `${(ex.strength + 1) * 20}%`, background: tone, transition: 'width 500ms ease-out' }} />
                </div>
                <div className="text-[12px] mt-1" style={{ color: tone }}>{ex.crack}</div>
              </>
            )}
          </button>
        );
      })}
      {done && <div className="text-[12px] text-center text-[#15803d] font-bold">Great work! Longer passwords are stronger.</div>}
    </section>
  );
};

/* ---------- Drag to order ---------- */

export const DragOrder = ({ config, onComplete, onScore }) => {
  const [placed, setPlaced] = useState([]);
  const [error, setError] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [hovering, setHovering] = useState(false);
  const dropRef = useRef(null);
  const remaining = config.tiles.filter((t) => !placed.some((p) => p.id === t.id));

  const attemptDrop = useCallback((tile) => {
    const nextExpected = config.order[placed.length];
    if (tile.id === nextExpected) {
      const next = [...placed, tile];
      setPlaced(next);
      onScore && onScore(5);
      if (next.length === config.order.length) onComplete && onComplete();
    } else {
      setError(true);
      setTimeout(() => setError(false), 400);
    }
  }, [placed, config.order, onScore, onComplete]);

  const isOverDropZone = (x, y) => {
    const el = dropRef.current;
    if (!el) return false;
    const r = el.getBoundingClientRect();
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  };

  const onTilePointerDown = (e, tile) => {
    if (e.button !== undefined && e.button !== 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setDragging({ tile, x: e.clientX, y: e.clientY, offX: e.clientX - rect.left, offY: e.clientY - rect.top, w: rect.width, h: rect.height, moved: false });
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      setDragging((d) => {
        if (!d) return d;
        const moved = d.moved || Math.abs(e.clientX - d.x) > 4 || Math.abs(e.clientY - d.y) > 4;
        return { ...d, x: e.clientX, y: e.clientY, moved };
      });
      setHovering(isOverDropZone(e.clientX, e.clientY));
    };
    const onUp = (e) => {
      const over = isOverDropZone(e.clientX, e.clientY);
      if (dragging.moved && over) attemptDrop(dragging.tile);
      setDragging(null);
      setHovering(false);
    };
    const onCancel = () => { setDragging(null); setHovering(false); };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
  }, [dragging, attemptDrop]);

  const onTileClick = (tile) => {
    if (dragging && dragging.moved) return;
    attemptDrop(tile);
  };

  return (
    <section className="rounded-[14px] p-4 bg-[#f8fafc] space-y-3" data-testid="youth-drag-order">
      <div className="p-display text-[15px]" style={{ color: '#0f2744', margin: 0 }}>{config.title}</div>
      <div className="text-[12px] text-[#64748b]">{config.subtitle}</div>
      <div
        ref={dropRef}
        data-testid="youth-drag-drop-zone"
        className="rounded-[10px] p-3 text-white transition-colors"
        style={{
          background: hovering ? '#0b3a66' : '#0f2744',
          border: hovering ? '2px dashed #f97316' : '2px dashed transparent',
          fontFamily: 'var(--p-font-mono)',
          fontSize: 12,
          minHeight: 56,
          animation: error ? 'youth-shake 320ms ease-in-out' : undefined,
        }}
      >
        <div className="text-[10px] uppercase tracking-widest text-[#94a3b8] mb-1">
          {dragging ? 'Drop tile here →' : 'Your page (drag or tap tiles below):'}
        </div>
        {placed.length === 0 ? <span className="text-[#94a3b8]">— empty —</span> : placed.map((p) => <span key={p.id} className="mr-1">{p.label}</span>)}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {remaining.map((t) => {
          const isBeingDragged = dragging?.tile.id === t.id && dragging.moved;
          return (
            <button
              key={t.id}
              type="button"
              onPointerDown={(e) => onTilePointerDown(e, t)}
              onClick={() => onTileClick(t)}
              data-testid={`youth-drag-tile-${t.id}`}
              className="p-mono text-[12px] rounded-[10px] p-2 border min-h-[40px] touch-none select-none transition-transform"
              style={{
                background: isBeingDragged ? 'rgba(249,115,22,0.15)' : '#fafaf9',
                borderColor: isBeingDragged ? '#f97316' : '#e2e8f0',
                cursor: 'grab',
                opacity: isBeingDragged ? 0.35 : 1,
              }}
              aria-grabbed={isBeingDragged}
            >{t.label}</button>
          );
        })}
      </div>
      {dragging && dragging.moved && (
        <div
          aria-hidden="true"
          data-testid="youth-drag-ghost"
          className="p-mono text-[12px] rounded-[10px] p-2 fixed z-[999] pointer-events-none shadow-lg"
          style={{
            left: dragging.x - dragging.offX,
            top: dragging.y - dragging.offY,
            width: dragging.w,
            height: dragging.h,
            background: '#fff',
            border: '2px solid #f97316',
            color: '#0f2744',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: 'rotate(-2deg)',
          }}
        >{dragging.tile.label}</div>
      )}
      {placed.length === config.order.length && config.preview && (
        <div className="rounded-[10px] p-3 bg-white border border-[#f97316]">
          <div className="text-[10px] uppercase tracking-widest text-[#f97316] font-bold mb-2">Rendered preview:</div>
          {config.preview.h1 && <div className="p-display text-[22px] leading-tight" style={{ color: '#0f2744' }}>{config.preview.h1}</div>}
          {config.preview.p && <div className="text-[14px] mt-1 text-[#0f2744]">{config.preview.p}</div>}
        </div>
      )}
    </section>
  );
};

/* ---------- Atom orbit ---------- */

export const AtomOrbit = ({ onDone }) => {
  useEffect(() => { const t = setTimeout(() => onDone && onDone(), 400); return () => clearTimeout(t); }, [onDone]);
  return (
    <div className="rounded-[14px] p-6 flex items-center justify-center" style={{ background: 'radial-gradient(circle at center,#1e3a5f 0%,#081827 100%)', minHeight: 200 }}>
      <div className="relative" style={{ width: 140, height: 140 }} aria-label="Atom animation">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full" style={{ background: 'radial-gradient(circle,#f97316,#c2410c)', boxShadow: '0 0 20px #f97316' }} aria-hidden="true" />
        {[0, 1, 2].map((i) => (
          <div key={i} className="absolute inset-0 rounded-full" style={{ border: '1px dashed rgba(250,250,249,0.25)', animation: `youth-orbit-${i} 2400ms linear infinite`, transform: `rotate(${i * 60}deg)` }} aria-hidden="true">
            <div className="absolute left-1/2 -translate-x-1/2 w-2 h-2 rounded-full" style={{ top: -3, background: '#facc15', boxShadow: '0 0 10px #facc15' }} />
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------- Journey map ---------- */

export const JourneyMap = ({ config, onDone }) => {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActive((a) => Math.min(a + 1, config.stops.length - 1)), 800);
    return () => clearInterval(t);
  }, [config.stops.length]);
  useEffect(() => {
    if (active === config.stops.length - 1) {
      const t = setTimeout(() => onDone && onDone(), 600);
      return () => clearTimeout(t);
    }
  }, [active, config.stops.length, onDone]);
  return (
    <section className="rounded-[14px] p-4 bg-[#f8fafc] space-y-3" aria-label="Power journey map">
      <div className="p-display text-[15px]" style={{ color: '#0f2744', margin: 0 }}>{config.title}</div>
      <div className="space-y-2">
        {config.stops.map((s, i) => {
          const isActive = i <= active;
          return (
            <div key={i} className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-[20px] shrink-0 transition-all" style={{ background: isActive ? '#f97316' : '#e2e8f0', color: isActive ? '#0f2744' : '#94a3b8', transform: isActive ? 'scale(1)' : 'scale(0.9)', opacity: isActive ? 1 : 0.5 }} aria-hidden="true">{s.icon}</div>
              <div className="flex-1 pt-1">
                <div className="text-[13px] font-bold" style={{ color: isActive ? '#0f2744' : '#94a3b8' }}>{s.title}</div>
                <div className="text-[12px] mt-0.5" style={{ color: isActive ? '#64748b' : '#cbd5e1' }}>{s.text}</div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

/* ---------- Repair sequence ---------- */

export const RepairSequence = ({ config, onComplete, onScore }) => {
  const [step, setStep] = useState(0);
  const [wrong, setWrong] = useState(null);
  const handle = (part) => {
    if (part === config.order[step]) {
      const next = step + 1;
      onScore && onScore(5);
      if (next >= config.order.length) { setStep(next); onComplete && onComplete(); }
      else setStep(next);
    } else {
      setWrong(part);
      setTimeout(() => setWrong(null), 400);
    }
  };
  const options = ['Power', 'Actuators', 'Sensors', 'Controller'];
  return (
    <section className="rounded-[14px] p-4 bg-[#f8fafc]">
      <div className="p-display text-[15px]" style={{ color: '#0f2744', margin: 0 }}>{config.title}</div>
      <div className="text-[12px] text-[#64748b] mt-1">{config.subtitle}</div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        {options.map((p) => {
          const doneIdx = config.order.indexOf(p);
          const isDone = doneIdx < step;
          const isNext = doneIdx === step;
          const shake = wrong === p;
          return (
            <button
              key={p}
              onClick={() => handle(p)}
              data-testid={`youth-repair-${p.toLowerCase()}`}
              disabled={isDone}
              className="min-h-[52px] rounded-[10px] px-3 py-2 text-[14px] font-bold"
              style={{
                background: isDone ? 'rgba(21,128,61,0.15)' : '#f1f5f9',
                color: isDone ? '#166534' : '#0f2744',
                border: isNext ? '2px solid #f97316' : '2px solid transparent',
                animation: shake ? 'youth-shake 320ms ease-in-out' : undefined,
              }}
            >
              {isDone && <Check className="w-4 h-4 inline mr-1" />} {p}
            </button>
          );
        })}
      </div>
      <div className="text-[11px] text-[#94a3b8] mt-2">{step >= config.order.length ? 'All parts checked!' : 'Next: check the easiest part first.'}</div>
    </section>
  );
};

/* ---------- Quiz ---------- */

const Quiz = ({ quiz, hearts, onHeartLoss, onQuestionCorrect, onDone }) => {
  const [idx, setIdx] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const q = quiz[idx];
  if (!q) { onDone(); return null; }

  const choose = (i) => {
    if (feedback && feedback.correct) return;
    const correct = q.options[i].c;
    if (correct) {
      const cheer = CHEER_LINES[Math.floor(Math.random() * CHEER_LINES.length)];
      setFeedback({ correct: true, chosen: i, cheer });
      onQuestionCorrect(10);
    } else {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setFeedback({ correct: false, chosen: i, revealed: newAttempts >= 2 });
      onHeartLoss();
    }
  };
  const next = () => {
    if (idx + 1 >= quiz.length) onDone();
    else { setIdx(idx + 1); setFeedback(null); setAttempts(0); }
  };
  const disabled = hearts <= 0;

  return (
    <section className="space-y-3">
      <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: '#f97316' }}>Quiz — Question {idx + 1} of {quiz.length}</div>
      <div className="p-display text-[17px]" style={{ color: '#0f2744', margin: 0 }}>{q.q}</div>
      <div className="space-y-2" role="radiogroup">
        {q.options.map((opt, i) => {
          const isChosen = feedback?.chosen === i;
          const isCorrect = opt.c;
          const revealed = feedback && (feedback.correct || feedback.revealed);
          let bg = '#f1f5f9', border = 'transparent';
          if (isChosen && feedback?.correct) { bg = 'rgba(21,128,61,0.12)'; border = '#15803d'; }
          else if (isChosen && !feedback?.correct) { bg = 'rgba(220,100,0,0.18)'; border = '#f97316'; }
          else if (revealed && isCorrect) { bg = 'rgba(21,128,61,0.10)'; border = '#15803d'; }
          return (
            <button
              key={i}
              disabled={disabled || (feedback && feedback.correct)}
              onClick={() => choose(i)}
              data-testid={`youth-answer-${i}`}
              className="w-full text-left rounded-[12px] px-4 py-3 text-[14px] min-h-[52px] flex items-center justify-between gap-3 transition-all text-[#0f2744]"
              style={{ background: bg, border: `1px solid ${border}`, animation: isChosen && !feedback?.correct ? 'youth-shake 320ms ease-in-out' : undefined }}
              aria-pressed={isChosen}
            >
              <span>{opt.t}</span>
              {isChosen && feedback?.correct && <Check className="w-5 h-5 text-[#15803d] shrink-0" aria-hidden="true" />}
            </button>
          );
        })}
      </div>
      {feedback?.correct && (
        <div className="rounded-[10px] p-3 text-[14px] font-semibold" style={{ background: 'rgba(21,128,61,0.10)', color: '#15803d' }} role="status" aria-live="polite">
          {feedback.cheer} <span className="p-mono">+10 XP</span>
        </div>
      )}
      {feedback && !feedback.correct && (
        <div className="rounded-[10px] p-3 text-[14px]" style={{ background: 'rgba(148,163,184,0.15)', color: '#475569' }} role="status" aria-live="polite">
          Not quite — try again!
          {feedback.revealed && q.explanation && <div className="text-[13px] mt-1 text-[#334155]">{q.explanation}</div>}
        </div>
      )}
      {feedback?.correct && (
        <Btn onClick={next} className="w-full" data-testid="youth-quiz-next">
          {idx + 1 >= quiz.length ? 'Finish' : 'Next question'} <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </Btn>
      )}
    </section>
  );
};

/* ---------- Node runner ---------- */

export const NodeRunner = ({ course, nodeIdx, onNodeDone, back }) => {
  const { state, update, fxSettings, toggleFx, restartCurrentNode } = useYouth();
  const node = course.nodes[nodeIdx];
  const [phase, setPhase] = useState('lesson');
  const [consequence, setConsequence] = useState(null);
  const xp = useXP();
  const { showToast } = useUI();
  const [cardsFlipped, setCardsFlipped] = useState(0);
  const [cardsRewarded, setCardsRewarded] = useState(false);
  const [choicePicked, setChoicePicked] = useState(null);
  const [placementPicked, setPlacementPicked] = useState(null);
  const cardsTotal = node.cards ? node.cards.length : 0;
  const [loopAdvanced, setLoopAdvanced] = useState(false);
  const [matchDone, setMatchDone] = useState(false);
  const [repairDone, setRepairDone] = useState(false);
  const [mathSolved, setMathSolved] = useState(false);
  const [meterDone, setMeterDone] = useState(false);
  const [dragDone, setDragDone] = useState(false);
  const [atomDone, setAtomDone] = useState(false);
  const [journeyDone, setJourneyDone] = useState(false);

  useEffect(() => {
    if (cardsTotal > 0 && cardsFlipped >= cardsTotal && !cardsRewarded) {
      setCardsRewarded(true);
      update((s) => ({ ...s, xp: (s.xp || 0) + 15 }));
      xp && xp.add(15);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardsFlipped, cardsTotal, cardsRewarded]);

  const awardXP = (amount, kind) => {
    update((s) => ({ ...s, xp: s.xp + amount }));
    xp && xp.add(amount, kind);
    fx.correct(fxSettings, course.id);
  };
  const loseHeart = () => {
    update((s) => ({ ...s, hearts: Math.max(0, s.hearts - 1) }));
    xp && xp.add(1, 'heart');
    fx.wrong(fxSettings, course.id);
  };

  const canAdvanceLesson = (() => {
    if (node.kind === 'story') return !!choicePicked;
    if (node.kind === 'math') return mathSolved;
    if (node.kind === 'cards') return cardsFlipped >= cardsTotal;
    if (node.kind === 'match') return matchDone;
    if (node.kind === 'loop') return loopAdvanced;
    if (node.kind === 'placement') return !!placementPicked;
    if (node.kind === 'repair') return repairDone;
    if (node.kind === 'meter') return meterDone;
    if (node.kind === 'drag') return dragDone;
    if (node.kind === 'atom') return atomDone;
    if (node.kind === 'journey') return journeyDone;
    if (node.kind === 'celebration') return true;
    return true;
  })();

  const handleChoice = (opt) => {
    if (opt.correct) {
      awardXP(25);
      setChoicePicked(opt);
      if (!node.quiz || node.quiz.length === 0) setTimeout(() => onNodeDone(), 800);
    } else {
      setConsequence(opt.consequence);
      setPhase('consequence');
    }
  };

  const handlePlacement = (card) => {
    awardXP(25);
    setPlacementPicked(card);
  };

  const handleQuizDone = () => {
    awardXP(25);
    if (state.hearts === 3) {
      setTimeout(() => xp && xp.add(50), 500);
      update((s) => ({ ...s, xp: s.xp + 50 }));
      showToast('Perfect quiz! +50 XP bonus', 'success');
    }
    setPhase('passed');
    setTimeout(() => onNodeDone(), 800);
  };

  return (
    <>
      <YouthHeader
        title={node.title}
        subtitle={course.title}
        onBack={back}
        onRestart={restartCurrentNode}
        breadcrumbs={[
          { label: 'Path', onClick: back },
          { label: course.title, onClick: back },
          { label: `Node ${nodeIdx + 1}` },
        ]}
        xp={state.xp}
        hearts={state.hearts}
        showHearts={course.hearts && phase === 'quiz'}
        fxSettings={fxSettings}
        onToggleFx={toggleFx}
      />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-4" data-testid={`youth-node-runner-${node.id}`}>
        {phase === 'consequence' && consequence && (
          <div className="rounded-[12px] p-5 text-center" style={{ background: 'rgba(249,115,22,0.10)', border: '1px solid #f97316' }}>
            <div className="text-[48px]" aria-hidden="true">{consequence.icon}</div>
            <p className="text-[15px] text-[#0f2744] mt-2 leading-relaxed">{consequence.text}</p>
            <Btn className="mt-4 w-full" onClick={() => setPhase('lesson')} data-testid="youth-retry">{consequence.retry} <ArrowRight className="w-4 h-4" /></Btn>
          </div>
        )}

        {phase === 'lesson' && (
          <>
            <LessonIllustration node={node} />
            <LessonBody node={node} />
            {node.stats && <StatCardList stats={node.stats} totalLine={node.totalLine} />}
            {node.math && <MathChallenge math={node.math} onSolved={() => { setMathSolved(true); awardXP(25); }} />}
            {node.cards && node.cards.length > 0 && (
              <div className="grid grid-cols-1 gap-3">
                {node.cards.map((c, i) => (<CardFlip key={i} card={c} onFlip={() => setCardsFlipped((n) => n + 1)} />))}
                {cardsFlipped >= cardsTotal && cardsFlipped > 0 && <div className="text-[13px] text-center text-[#15803d] font-bold">All cards read! ⚡ +15 XP</div>}
              </div>
            )}
            {node.loop && <LoopDiagram steps={node.loop} />}
            {node.loop && !loopAdvanced && <Btn className="w-full" onClick={() => { setLoopAdvanced(true); awardXP(15); }} data-testid="youth-loop-continue">Got it — continue <ArrowRight className="w-4 h-4" /></Btn>}
            {node.match && <MatchGame config={node.match} courseId={course.id} onScore={(n) => awardXP(n)} onAllMatched={() => setMatchDone(true)} />}
            {node.repair && <RepairSequence config={node.repair} onComplete={() => setRepairDone(true)} onScore={(n) => awardXP(n)} />}
            {node.meter && <PasswordMeter config={node.meter} onDone={() => { if (!meterDone) { setMeterDone(true); awardXP(15); } }} />}
            {node.drag && <DragOrder config={node.drag} onScore={(n) => awardXP(n)} onComplete={() => { if (!dragDone) { setDragDone(true); awardXP(20); } }} />}
            {node.atom && <AtomOrbit onDone={() => setAtomDone(true)} />}
            {node.journey && <JourneyMap config={node.journey} onDone={() => { if (!journeyDone) { setJourneyDone(true); awardXP(15); } }} />}
            {node.choice && (
              <section className="space-y-3">
                <div className="p-display text-[16px]" style={{ color: '#0f2744', margin: 0 }}>{node.choice.prompt}</div>
                <div className="grid grid-cols-1 gap-2">
                  {node.choice.options.map((opt) => {
                    const picked = choicePicked?.id === opt.id;
                    return (
                      <button key={opt.id} onClick={() => handleChoice(opt)} data-testid={`youth-choice-${opt.id}`} disabled={!!choicePicked && picked} className="text-left rounded-[12px] p-4 border-2 transition-all" style={{ background: picked ? 'rgba(21,128,61,0.10)' : '#f1f5f9', borderColor: picked ? '#15803d' : opt.correct ? '#f97316' : '#e2e8f0' }}>
                        <div className="text-[14px] font-semibold text-[#0f2744]">{opt.title}</div>
                      </button>
                    );
                  })}
                </div>
                {choicePicked?.cheer && <div className="text-center text-[14px] font-bold" style={{ color: '#15803d' }}>{choicePicked.cheer}</div>}
              </section>
            )}
            {node.kind === 'placement' && node.cards && (
              <section className="space-y-3">
                <div className="p-display text-[15px]" style={{ color: '#0f2744', margin: 0 }}>Pick either option — both are great!</div>
                <div className="grid grid-cols-1 gap-2">
                  {node.cards.map((c, i) => (
                    <button key={i} onClick={() => handlePlacement(c)} disabled={!!placementPicked} data-testid={`youth-place-${i}`} className="text-left rounded-[12px] p-4 border-2 transition-all" style={{ background: placementPicked === c ? 'rgba(21,128,61,0.10)' : '#f1f5f9', borderColor: placementPicked === c ? '#15803d' : '#e2e8f0' }}>
                      <div className="text-[14px] font-semibold text-[#0f2744]">{c.title}</div>
                      <ul className="mt-2 list-none p-0 space-y-1">{c.bullets.map((b, j) => (<li key={j} className="text-[12px] text-[#64748b]">• {b}</li>))}</ul>
                    </button>
                  ))}
                </div>
                {placementPicked && <div className="rounded-[10px] p-3 text-[14px] font-semibold text-center" style={{ background: 'rgba(21,128,61,0.10)', color: '#15803d' }}>{placementPicked.success}</div>}
              </section>
            )}
            {canAdvanceLesson && node.quiz && node.quiz.length > 0 && <Btn className="w-full" onClick={() => setPhase('quiz')} data-testid="youth-start-quiz">Take the quiz <ArrowRight className="w-4 h-4" aria-hidden="true" /></Btn>}
            {canAdvanceLesson && (!node.quiz || node.quiz.length === 0) && node.kind !== 'story' && <Btn className="w-full" onClick={onNodeDone} data-testid="youth-node-continue">Continue <ArrowRight className="w-4 h-4" aria-hidden="true" /></Btn>}
          </>
        )}

        {phase === 'quiz' && (
          state.hearts <= 0 ? (
            <div className="rounded-[12px] p-6 text-center" style={{ background: '#f1f5f9' }}>
              <div className="text-[48px]" aria-hidden="true">💤</div>
              <h2 className="p-display text-[18px] mt-2" style={{ color: '#0f2744', margin: 0 }}>Take a break!</h2>
              <p className="text-[14px] text-[#64748b] mt-2 leading-relaxed">Come back tomorrow to try again. Your hearts refill overnight.</p>
              <Btn variant="ghost" onClick={() => update((s) => ({ ...s, hearts: 3 }))} className="mt-4 w-full" data-testid="youth-reset-hearts"><RotateCcw className="w-4 h-4 mr-1" /> Reset hearts (demo)</Btn>
            </div>
          ) : (
            <Quiz quiz={node.quiz} hearts={state.hearts} onHeartLoss={loseHeart} onQuestionCorrect={(n) => awardXP(n)} onDone={handleQuizDone} />
          )
        )}

        {phase === 'passed' && (
          <div className="rounded-[12px] p-6 text-center text-white" style={{ background: 'linear-gradient(140deg,#f97316,#c2410c)' }}>
            <Trophy className="w-10 h-10 mx-auto" aria-hidden="true" />
            <div className="p-display text-[20px] mt-2" style={{ color: '#0f2744', margin: 0 }}>Level {nodeIdx + 1} Complete! ⭐</div>
          </div>
        )}
      </main>
    </>
  );
};

/* ---------- Badge unlock ---------- */

export const BadgeUnlock = ({ course, onContinue }) => {
  const [showContinue, setShowContinue] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShowContinue(true), 1500);
    return () => clearTimeout(t);
  }, []);
  const confetti = Array.from({ length: 20 });
  return (
    <div className="absolute inset-0 z-[200] flex items-center justify-center px-6" role="dialog" aria-modal="true" data-testid="youth-badge-unlock">
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.72)' }} />
      <div className="relative flex flex-col items-center text-center">
        <div className="relative">
          <div
            className="w-32 h-32 rounded-full flex items-center justify-center text-[64px]"
            style={{
              background: `radial-gradient(circle at center, ${course.badge.color} 0%, #f97316 100%)`,
              animation: 'youth-badge-scale 600ms cubic-bezier(0.4, 1.6, 0.5, 1) both',
              boxShadow: `0 20px 60px ${course.badge.color}66`,
            }}
            aria-hidden="true"
          >{course.badge.icon}</div>
          <div className="absolute inset-0 rounded-full pointer-events-none" style={{ border: `4px solid ${course.badge.color}`, animation: 'youth-ring-pulse 800ms ease-out 400ms both' }} aria-hidden="true" />
          {confetti.map((_, i) => {
            const colors = ['#f97316', '#fafaf9', '#0f2744', '#f59e0b'];
            const c = colors[i % colors.length];
            const angle = (i / confetti.length) * Math.PI * 2;
            const dist = 120 + Math.random() * 80;
            const x = Math.cos(angle) * dist;
            const y = Math.sin(angle) * dist + 60;
            return (
              <div key={i} className="absolute left-1/2 top-1/2 w-2 h-2 rounded-full" style={{ background: c, transform: 'translate(-50%,-50%)', animation: `youth-confetti 1200ms ease-out ${300 + i * 15}ms both`, '--cx': `${x}px`, '--cy': `${y}px` }} aria-hidden="true" />
            );
          })}
        </div>
        <div className="mt-6 text-[13px] font-bold uppercase tracking-widest" style={{ color: '#f59e0b' }}>You earned it!</div>
        <h2 className="p-display text-[24px] mt-1" style={{ color: '#fafaf9', margin: 0 }}>{course.badge.name}</h2>
        <div className="mt-3 p-mono text-[16px] font-bold" style={{ color: '#f97316', animation: 'youth-xp-float 1200ms ease-out 900ms both' }}>+100 XP</div>
        {showContinue && (
          <Btn className="mt-6 w-full max-w-[280px]" onClick={onContinue} data-testid="youth-badge-continue">Continue <ArrowRight className="w-4 h-4" /></Btn>
        )}
      </div>
    </div>
  );
};

/* ---------- Level celebration ---------- */

export const LevelCelebration = ({ course, onContinue, onShare }) => {
  const stars = Array.from({ length: 10 });
  return (
    <div className="absolute inset-0 z-[190] flex flex-col items-center justify-center px-4 py-6 overflow-hidden" style={{ background: 'linear-gradient(120deg,#0f2744 0%,#1e3a5f 100%)' }} data-testid="youth-level-celebration">
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(90deg,transparent 0%,rgba(249,115,22,0.35) 50%,transparent 100%)', animation: 'youth-sweep 900ms ease-out' }} aria-hidden="true" />
      {stars.map((_, i) => {
        const size = 12 + Math.random() * 18;
        const left = Math.random() * 90 + 5;
        return (
          <div key={i} className="absolute top-0" style={{ left: `${left}%`, fontSize: size, animation: `youth-star-fall 1800ms ease-in ${i * 90}ms both`, color: '#f97316', textShadow: '0 2px 10px rgba(249,115,22,0.6)' }} aria-hidden="true">★</div>
        );
      })}
      <div className="relative text-center">
        <div className="text-[68px]" aria-hidden="true">{course.badge.icon}</div>
        <h1 className="p-display text-[22px] mt-2" style={{ color: '#fafaf9', margin: 0 }}>Course Complete!</h1>
        <div className="text-[13px] mt-1 font-bold uppercase tracking-widest" style={{ color: '#f97316' }}>{course.badge.name} unlocked</div>
      </div>
      <div className="relative w-full max-w-[320px] mt-6 rounded-[12px] p-5 text-center" style={{ background: '#fafaf9', animation: 'youth-cert-slide 600ms ease-out 300ms both' }}>
        <div className="text-[10px] font-bold uppercase tracking-widest text-[#c2410c]">Certificate</div>
        <h2 className="p-display text-[18px] mt-1" style={{ color: '#0f2744', margin: 0 }}>{course.title}</h2>
        <div className="text-[13px] text-[#64748b] mt-2">Learner: <strong style={{ color: '#0f2744' }}>Jordan M.</strong></div>
        <div className="text-[13px] text-[#64748b]">Skill Earned: <strong style={{ color: '#0f2744' }}>{course.credential}</strong></div>
        <div className="text-[13px] text-[#64748b]">Date: <span className="p-mono">{new Date().toLocaleDateString()}</span></div>
        <div className="mt-3 p-mono text-[14px]" style={{ color: '#f97316' }}>+100 XP Badge Bonus</div>
      </div>
      <div className="relative mt-4 grid grid-cols-2 gap-2 w-full max-w-[320px]">
        <Btn variant="ghost" onClick={onShare} data-testid="youth-share-cert">Share</Btn>
        <Btn onClick={onContinue} data-testid="youth-cert-continue">Next <ArrowRight className="w-4 h-4" /></Btn>
      </div>
    </div>
  );
};
